import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '../../hooks/useAuth';
import { RESOURCE_CHANNELS } from '../../config/resourceHub';

interface MarketingLayoutProps {
  children: React.ReactNode;
}

const serviceItems = [
  { to: '/service/warehouse-3pl', label: '海外仓/仓配托管' },
  { to: '/service/transit-consolidation', label: '包裹转运/集运' },
  { to: '/service/courier-logistics', label: '快递物流' },
  { to: '/service/procurement-sales', label: '采购/销售代理' },
];

const resourceItems = RESOURCE_CHANNELS.map((c) => ({ to: `/resources/${c.routeSlug}`, label: c.label }));

// 顶部导航顺序：首页 → 服务板块与收费标准 → 常见问题 → 资源中心 → 关于我们 → 系统使用指南 → 联系我们
// 服务板块与资源中心是下拉菜单，单独用 JSX 渲染，所以这里按它们中间穿插的位置拆成两组。
const navItemsBeforeResources = [{ to: '/faq', label: '常见问题' }];
const navItemsAfterResources = [
  { to: '/about', label: '关于我们' },
  { to: '/guide', label: '系统使用指南' },
  { to: '/contact', label: '联系我们' },
];

// 页脚法律/合规链接。以后再加新的法律文档（如 Cookie 政策），只需要在后台"通用内容"
// 里新建一条记录、这里加一行，不需要再动页脚布局结构。
// 服务条款目前还没有对应的后台内容记录，slug 先占位，需要在后台创建同 slug 的内容后才会有实际页面。
const footerLinks = [
  { to: '/content/privacy-policy', label: '隐私政策' },
  { to: '/content/terms-of-service', label: '服务条款' },
  { to: '/contact', label: '联系我们' },
];

const MarketingLayoutInner: React.FC<MarketingLayoutProps> = ({ children }) => {
  const location = useLocation();
  const { user, displayName, logout, isLoading } = useAuth();
  const [serviceMenuOpen, setServiceMenuOpen] = useState(false);
  const serviceMenuActive =
    location.pathname.startsWith('/service/') ||
    location.pathname === '/services' ||
    location.pathname === '/pricing';
  const [resourceMenuOpen, setResourceMenuOpen] = useState(false);
  const resourceMenuActive = location.pathname.startsWith('/resources');

  useEffect(() => {
    setServiceMenuOpen(false);
    setResourceMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <header className="sticky top-0 z-10 border-b border-[#d9eaff] bg-white/95 backdrop-blur">
        <div className="w-full px-6 py-4 xl:px-10">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="shrink-0">
            <div className="flex items-center gap-3">
              <img
                src="/anfei-logo.png"
                alt="安飞供应链 Logo"
                className="h-10 w-auto object-contain"
              />
              <div className="flex flex-col items-start leading-tight">
                <div className="text-2xl font-bold tracking-tight text-[#3D92F9]">安飞供应链</div>
                <div className="mt-1 text-sm font-medium text-[#6eaefb]">AnFei Supply Chain</div>
              </div>
            </div>
            </div>

            <nav className="flex flex-wrap items-center gap-2 lg:flex-1 lg:justify-center">
            <Link
              to="/"
              className={`rounded-lg border px-4 py-2.5 text-base font-medium transition ${
                location.pathname === '/'
                  ? 'border-[#cfe5ff] bg-[#eef6ff] text-[#2f82e8]'
                  : 'border-transparent text-gray-700 hover:border-[#e2eeff] hover:bg-[#f6f9ff] hover:text-[#2f82e8]'
              }`}
            >
              首页
            </Link>
            <div
              className="relative"
              onMouseEnter={() => setServiceMenuOpen(true)}
              onMouseLeave={() => setServiceMenuOpen(false)}
            >
              <Link
                to={serviceItems[0].to}
                onClick={() => setServiceMenuOpen(false)}
                className={`inline-flex items-center rounded-lg border px-4 py-2.5 text-base font-medium transition ${
                  serviceMenuActive
                    ? 'border-[#cfe5ff] bg-[#eef6ff] text-[#2f82e8]'
                    : 'border-transparent text-gray-700 hover:border-[#e2eeff] hover:bg-[#f6f9ff] hover:text-[#2f82e8]'
                }`}
              >
                服务板块与收费标准
              </Link>
              <div
                className={`absolute left-0 top-full z-20 w-60 rounded-lg border border-[#d9eaff] bg-white p-1 shadow-lg transition duration-150 ${
                  serviceMenuOpen ? 'visible opacity-100' : 'invisible opacity-0'
                }`}
              >
                {serviceItems.map((item) => {
                  const active = location.pathname === item.to;
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setServiceMenuOpen(false)}
                      className={`block rounded-md px-3 py-2 text-sm transition ${
                        active
                          ? 'bg-[#eef6ff] text-[#2f82e8]'
                          : 'text-gray-700 hover:bg-[#f6f9ff] hover:text-[#2f82e8]'
                      }`}
                    >
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            </div>
            {navItemsBeforeResources.map((item) => {
              const active = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`rounded-lg border px-4 py-2.5 text-base font-medium transition ${
                    active
                      ? 'border-[#cfe5ff] bg-[#eef6ff] text-[#2f82e8]'
                      : 'border-transparent text-gray-700 hover:border-[#e2eeff] hover:bg-[#f6f9ff] hover:text-[#2f82e8]'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
            <div
              className="relative"
              onMouseEnter={() => setResourceMenuOpen(true)}
              onMouseLeave={() => setResourceMenuOpen(false)}
            >
              <Link
                to="/resources"
                onClick={() => setResourceMenuOpen(false)}
                className={`inline-flex items-center rounded-lg border px-4 py-2.5 text-base font-medium transition ${
                  resourceMenuActive
                    ? 'border-[#cfe5ff] bg-[#eef6ff] text-[#2f82e8]'
                    : 'border-transparent text-gray-700 hover:border-[#e2eeff] hover:bg-[#f6f9ff] hover:text-[#2f82e8]'
                }`}
              >
                资源中心
              </Link>
              <div
                className={`absolute left-0 top-full z-20 w-48 rounded-lg border border-[#d9eaff] bg-white p-1 shadow-lg transition duration-150 ${
                  resourceMenuOpen ? 'visible opacity-100' : 'invisible opacity-0'
                }`}
              >
                {resourceItems.map((item) => {
                  const active = location.pathname.startsWith(item.to);
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setResourceMenuOpen(false)}
                      className={`block rounded-md px-3 py-2 text-sm transition ${
                        active
                          ? 'bg-[#eef6ff] text-[#2f82e8]'
                          : 'text-gray-700 hover:bg-[#f6f9ff] hover:text-[#2f82e8]'
                      }`}
                    >
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            </div>
            {navItemsAfterResources.map((item) => {
              const active = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`rounded-lg border px-4 py-2.5 text-base font-medium transition ${
                    active
                      ? 'border-[#cfe5ff] bg-[#eef6ff] text-[#2f82e8]'
                      : 'border-transparent text-gray-700 hover:border-[#e2eeff] hover:bg-[#f6f9ff] hover:text-[#2f82e8]'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
            </nav>

            {!isLoading && user ? (
              <div className="flex shrink-0 items-center gap-2 whitespace-nowrap">
                <span className="max-w-[180px] truncate text-sm text-gray-700">{displayName}</span>
                <Link
                  to="/dashboard/select-module"
                  className="rounded border border-[#3D92F9] px-3 py-2 text-sm font-medium text-[#3D92F9] hover:bg-[#eef6ff]"
                >
                  进入后台
                </Link>
                <button
                  type="button"
                  onClick={() => logout('/')}
                  className="rounded bg-[#3D92F9] px-3 py-2 text-sm font-medium text-white hover:bg-[#2f82e8]"
                >
                  退出
                </button>
              </div>
            ) : (
              <div className="flex shrink-0 items-center gap-2 whitespace-nowrap">
                <Link
                  to="/register"
                  className="rounded border border-[#3D92F9] px-3 py-2 text-sm font-medium text-[#3D92F9] hover:bg-[#eef6ff]"
                >
                  注册
                </Link>
                <Link
                  to="/login"
                  className="rounded bg-[#3D92F9] px-3 py-2 text-sm font-medium text-white hover:bg-[#2f82e8]"
                >
                  登录
                </Link>
              </div>
            )}
          </div>
        </div>
      </header>

      <main>{children}</main>

      <footer className="border-t border-gray-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-6 py-6 text-sm text-gray-600 md:flex-row md:items-center md:justify-between">
          <span>© 安飞供应链 AnFei Supply Chain</span>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {footerLinks.map((item) => (
              <Link key={item.to} to={item.to} className="hover:text-blue-600 hover:underline">
                {item.label}
              </Link>
            ))}
          </div>

          <span>官网：www.anfeihub.com</span>
        </div>
      </footer>
    </div>
  );
};

const MarketingLayout: React.FC<MarketingLayoutProps> = ({ children }) => {
  return (
    <AuthProvider>
      <MarketingLayoutInner>{children}</MarketingLayoutInner>
    </AuthProvider>
  );
};

export default MarketingLayout;
